import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HR_LEAVE_PERIOD_CLOSED, HR_LEAVE_PERIOD_PROPOSED } from '@app/shared/constants';
import {
	LeaveClosingForm, LeaveClosingRow, daysLabel, leaveClosingIssue, leavePeriodLabel,
} from '../../hr-leave-closing';
import { LeaveRules } from '../../hr-leave-rights';

export interface ClosingDialogData {
	row: LeaveClosingRow;
	employee: string;
	rules: LeaveRules;
}

export interface ClosingDialogResult extends LeaveClosingForm {
	/// `HR_LEAVE_PERIOD_PROPOSED` = on soumet le compte au salarié ;
	/// `HR_LEAVE_PERIOD_CLOSED`   = on arrête le compte séance tenante.
	state: number;
}

// ARRÊTÉ DE COMPTE D'UNE ANNÉE DE CONGÉS — le formulaire de validation des CP restants sur N-1.
//
// Le calcul est fait et affiché : report entrant, acquis, pris, solde. Ce que le formulaire
// demande, c'est la seule chose qu'aucun logiciel ne peut décider — CE QU'ON FAIT du solde.
//
// Trois destinations, et elles doivent s'épuiser mutuellement :
//   REPORTÉ  sur l'année suivante. C'est la pratique la plus courante, ce n'est pas un droit
//            automatique : le report suppose un accord, un usage, ou une impossibilité de prendre
//            les congés du fait de l'employeur (maladie : 15 mois, Cass. soc. 2024).
//   PERDU    la règle de principe (L3141-12 s.) quand rien n'a empêché de poser ses congés.
//   PAYÉ     l'indemnité compensatrice — en pratique, la rupture du contrat.
//
// Le formulaire refuse une répartition qui ne fait pas le solde : des jours qui « disparaissent »
// entre deux cases sont précisément ce qu'un litige vient chercher trois ans plus tard.
@Component({
	selector: 'app-hr-closing-dialog',
	standalone: true,
	imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule,
		MatButtonModule, MatIconModule, MatTooltipModule],
	templateUrl: './closing-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './closing-dialog.component.scss',
})
export class ClosingDialogComponent {

	public data: ClosingDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<ClosingDialogComponent>);

	public carried = 0;
	public lost = 0;
	public paid = 0;
	public comment = '';

	public readonly daysLabel = daysLabel;

	constructor() {
		// Une clôture DÉJÀ enregistrée se rouvre telle qu'elle a été saisie — on corrige un arrêté,
		// on n'en repart pas de zéro. Sinon, la proposition de l'établissement.
		const c = this.data.row.closing;
		if (c) {
			this.carried = +c.carried_days || 0;
			this.lost = +c.lost_days || 0;
			this.paid = +c.paid_days || 0;
			this.comment = c.comment ?? '';
		} else {
			this.carried = this.data.row.proposed_carry;
			this.lost = this.data.row.proposed_lost;
		}
	}

	get period(): string { return leavePeriodLabel(this.data.row.period); }

	get balance(): number { return Math.max(0, this.data.row.balance_days); }

	get form(): LeaveClosingForm {
		return { carried_days: +this.carried || 0, lost_days: +this.lost || 0, paid_days: +this.paid || 0, comment: this.comment };
	}

	/// Ce qu'il reste à placer — affiché en permanence, parce que c'est la seule chose que la
	/// personne cherche des yeux en tapant.
	get remaining(): number {
		const f = this.form;
		return Math.round((this.balance - f.carried_days - f.lost_days - f.paid_days) * 100) / 100;
	}

	/// D'où vient le pré-remplissage — la fiche du salarié, ou la règle de l'établissement.
	/// Une clôture déjà enregistrée ne PROPOSE plus rien : on relit ce qui a été décidé.
	get policyLabel(): string {
		if (this.data.row.closing) return 'Arrêté déjà enregistré — vous le corrigez.';
		const what = this.data.row.policy === 'lost' ? 'perdus' : 'reportés sur l\'année suivante';
		return this.data.row.policy_own
			? 'Proposition d\'après la règle propre à ce salarié : ' + what + '.'
			: 'Proposition d\'après la règle de l\'établissement : ' + what + '.';
	}

	get issue(): string | null { return leaveClosingIssue(this.form, this.data.row); }

	get valid(): boolean { return this.issue === null; }

	/// « Tout reporter » / « Tout perdre » : les deux gestes qui couvrent 95 % des cas, en un clic.
	allTo(where: 'carry' | 'lost' | 'paid'): void {
		this.carried = where === 'carry' ? this.balance : 0;
		this.lost = where === 'lost' ? this.balance : 0;
		this.paid = where === 'paid' ? this.balance : 0;
	}

	/// Soumettre au salarié (état 0) plutôt qu'arrêter : le compte lui est proposé, il le valide
	/// depuis son écran Congés, et le responsable clôture ensuite en connaissance de cause.
	onPropose(): void { this.close(HR_LEAVE_PERIOD_PROPOSED); }

	onClose(): void { this.close(HR_LEAVE_PERIOD_CLOSED); }

	private close(state: number): void {
		if (!this.valid) return;
		this.dialogRef.close({ ...this.form, comment: (this.comment ?? '').trim() || null, state } as ClosingDialogResult);
	}

	onCancel(): void { this.dialogRef.close(); }
}
